import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaSearch, FaLightbulb, FaCalculator, FaChartLine } from 'react-icons/fa';

const QuantumNumbersCalculator = () => {
  const [principal, setPrincipal] = useState('');
  const [azimuthal, setAzimuthal] = useState('');
  const [magnetic, setMagnetic] = useState('');
  const [spin, setSpin] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateQuantumNumbers = () => {
    setError('');
    
    // Validate principal quantum number (n)
    const n = parseInt(principal);
    if (isNaN(n) || n <= 0) {
      setError('Principal quantum number (n) must be a positive integer');
      return;
    }

    // Validate azimuthal quantum number (l)
    const l = parseInt(azimuthal);
    if (isNaN(l) || l < 0 || l >= n) {
      setError('Azimuthal quantum number (l) must be an integer between 0 and n-1');
      return;
    }

    // Validate magnetic quantum number (m_l)
    const ml = parseInt(magnetic);
    if (isNaN(ml) || ml < -l || ml > l) {
      setError('Magnetic quantum number (m_l) must be an integer between -l and l');
      return;
    }

    // Validate spin quantum number (m_s)
    const ms = parseFloat(spin);
    if (ms !== 0.5 && ms !== -0.5) {
      setError('Spin quantum number (m_s) must be either +1/2 or -1/2');
      return;
    }

    // Calculate orbital information
    const orbitalType = ['s', 'p', 'd', 'f'][l] || 'g';
    const orbitalName = `${n}${orbitalType}`;
    const maxElectrons = 2 * (2 * l + 1);
    const currentElectrons = ml === 0 ? 1 : 2;

    const result = {
      orbitalName,
      orbitalType,
      maxElectrons,
      currentElectrons,
      orbitalShape: getOrbitalShape(l)
    };

    setResult(result);
    setShowGraph(true);
  };

  const generateGraphData = () => {
    if (!principal || !azimuthal) return null;

    const n = parseInt(principal);
    const l = parseInt(azimuthal);
    
    // Generate data for electron distribution
    const series = [
      {
        name: 'Electron Distribution',
        data: Array.from({ length: n }, (_, i) => ({
          x: `n=${i + 1}`,
          y: 2 * (2 * l + 1)
        }))
      }
    ];

    const options = {
      chart: {
        type: 'bar',
        height: 400,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: true,
          columnWidth: '50%'
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        type: 'category',
        title: {
          text: 'Energy Level (n)',
        },
        labels: {
          style: {
            colors: '#4B5563',
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        title: {
          text: 'Maximum Electrons',
        },
        labels: {
          style: {
            colors: '#4B5563',
            fontSize: '12px'
          }
        }
      },
      title: {
        text: 'Electron Distribution Across Energy Levels',
        align: 'center',
        style: {
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#1F2937'
        }
      },
      colors: ['#4F46E5'],
      grid: {
        borderColor: '#E5E7EB',
        strokeDashArray: 2
      }
    };

    return { series, options };
  };

  const getOrbitalShape = (l) => {
    switch (l) {
      case 0: return 'Spherical (s)';
      case 1: return 'Dumbbell (p)';
      case 2: return 'Complex (d)';
      case 3: return 'Very Complex (f)';
      default: return 'Higher Order';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100">
      <Helmet>
        <title>Quantum Numbers Calculator | Physics Tool</title>
        <meta name="description" content="Calculate quantum numbers (n, l, m_l, m_s) for electrons in an atom. Interactive calculator with orbital visualization for understanding atomic structure." />
        <meta name="keywords" content="quantum numbers calculator, atomic structure calculator, physics calculator, electron configuration calculator, quantum mechanics calculator" />
        <meta property="og:title" content="Quantum Numbers Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate quantum numbers (n, l, m_l, m_s) for electrons in an atom. Interactive calculator with orbital visualization for understanding atomic structure." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Quantum Numbers Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate quantum numbers (n, l, m_l, m_s) for electrons in an atom. Interactive calculator with orbital visualization for understanding atomic structure." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/quantum-numbers" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-purple-600 mb-4">
            <FaSearch />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Quantum Numbers Calculator</h1>
          <p className="text-xl text-gray-600">Calculate Electron Quantum Numbers and Understand Atomic Structure</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Principal Quantum Number (n)</label>
                <input
                  type="number"
                  value={principal}
                  onChange={(e) => setPrincipal(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter principal quantum number"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Azimuthal Quantum Number (l)</label>
                <input
                  type="number"
                  value={azimuthal}
                  onChange={(e) => setAzimuthal(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter azimuthal quantum number"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Magnetic Quantum Number (m_l)</label>
                <input
                  type="number"
                  value={magnetic}
                  onChange={(e) => setMagnetic(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter magnetic quantum number"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Spin Quantum Number (m_s)</label>
                <select
                  value={spin}
                  onChange={(e) => setSpin(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                >
                  <option value="">Select spin quantum number</option>
                  <option value="0.5">+1/2</option>
                  <option value="-0.5">-1/2</option>
                </select>
              </div>
              <div>
                <button
                  onClick={calculateQuantumNumbers}
                  className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Quantum Numbers
                </button>
              </div>

              {error && (
                <div className="mt-4 p-4 bg-red-100 rounded">
                  <p className="text-red-800">{error}</p>
                </div>
              )}

              {result && (
                <div className="mt-4 p-4 bg-purple-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-purple-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-purple-800">Results</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-purple-800">Orbital Name: {result.orbitalName}</p>
                        <p className="text-purple-800">Orbital Type: {result.orbitalType}</p>
                        <p className="text-purple-800">Maximum Electrons: {result.maxElectrons}</p>
                        <p className="text-purple-800">Orbital Shape: {result.orbitalShape}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-purple-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Quantum numbers describe the state of an electron in an atom:
                      <br />
                      <br />
                      n = {principal} (Principal quantum number)
                      <br />
                      l = {azimuthal} (Azimuthal quantum number)
                      <br />
                      m_l = {magnetic} (Magnetic quantum number)
                      <br />
                      m_s = {spin} (Spin quantum number)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Electron Distribution</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="bar"
                height={400}
              />
              <p className="mt-4 text-gray-600 text-center">
                Shows maximum electrons that can be accommodated in each energy level
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the principal quantum number (n) - represents the energy level of the electron</li>
            <li>Enter the azimuthal quantum number (l) - represents the shape of the orbital</li>
            <li>Enter the magnetic quantum number (m_l) - represents the orientation of the orbital</li>
            <li>Select the spin quantum number (m_s) - represents the spin direction of the electron</li>
            <li>Click "Calculate Quantum Numbers" to get the results</li>
            <li>View the orbital properties and electron distribution graph</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Comprehensive Calculations</h3>
              <p>Calculate all four quantum numbers and their relationships</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Visual Learning</h3>
              <p>Interactive graph showing electron distribution across energy levels</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for students learning quantum mechanics and atomic structure</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Instant Feedback</h3>
              <p>Real-time validation and error messages for incorrect inputs</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are Quantum Numbers?</h3>
              <p className="text-gray-600">
                Quantum numbers are a set of four numbers that describe the state of an electron in an atom:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Principal Quantum Number (n): Energy level</li>
                  <li>Azimuthal Quantum Number (l): Orbital shape</li>
                  <li>Magnetic Quantum Number (m_l): Orbital orientation</li>
                  <li>Spin Quantum Number (m_s): Electron spin</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What does the graph show?</h3>
              <p className="text-gray-600">
                The graph shows:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Maximum electrons that can be accommodated in each energy level</li>
                  <li>How electron capacity increases with energy levels</li>
                  <li>Relationship between principal quantum number and electron capacity</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are the rules for quantum numbers?</h3>
              <p className="text-gray-600">
                The rules for quantum numbers are:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>n must be a positive integer (1, 2, 3, ...)</li>
                  <li>l ranges from 0 to n-1</li>
                  <li>m_l ranges from -l to +l</li>
                  <li>m_s can only be +1/2 or -1/2</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why are quantum numbers important?</h3>
              <p className="text-gray-600">
                Quantum numbers are crucial because they:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Determine electron configuration in atoms</li>
                  <li>Explain chemical properties of elements</li>
                  <li>Help predict molecular structure and bonding</li>
                  <li>Are fundamental to understanding atomic physics</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                Quantum numbers are used in:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Chemistry: Understanding periodic table and chemical bonding</li>
                  <li>Physics: Studying atomic structure and spectroscopy</li>
                  <li>Materials Science: Designing new materials</li>
                  <li>Quantum Computing: Developing quantum algorithms</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuantumNumbersCalculator;
